// src/components/Prospectos.js
import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc, arrayUnion, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaWhatsapp, FaPlus, FaEdit, FaSave, FaTimes, FaPlusCircle } from 'react-icons/fa';
import { format } from 'date-fns';

const Prospectos = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [isRegisterVisit, setIsRegisterVisit] = useState(false);
  const [visitDate, setVisitDate] = useState('');
  const [visitNotes, setVisitNotes] = useState('');
  const [isEditingState, setIsEditingState] = useState(false);
  const [newState, setNewState] = useState('');
  const [isAddingProspect, setIsAddingProspect] = useState(false);
  const [newProspect, setNewProspect] = useState({
    businessName: '',
    phone: '',
    state: 'Entrante'
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'prospects'), (snapshot) => {
      const prospectsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProspects(prospectsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const groupedByState = prospects.reduce((acc, prospect) => {
    const { state } = prospect;
    if (!acc[state]) {
      acc[state] = [];
    }
    acc[state].push(prospect);
    return acc;
  }, {});

  const stateOrder = ['Entrante', 'Prospecto calificado', 'En espera de visita', 'Perdido', 'Ganado'];
  const orderedStates = stateOrder.filter(state => groupedByState[state]);

  const openDrawer = (prospect) => {
    setSelectedProspect(prospect);
    setIsDrawerOpen(true);
    setIsRegisterVisit(false);
    setIsAddingProspect(false); // Reset add prospect state when opening a new drawer
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedProspect(null);
    setIsRegisterVisit(false);
    setIsEditingState(false);
    setIsAddingProspect(false); // Reset add prospect state when closing the drawer
  };

  const handleRegisterVisit = async () => {
    if (!visitDate || !visitNotes) {
      alert('Please fill in all fields.');
      return;
    }

    const newVisit = {
      date: visitDate,
      notes: visitNotes,
      createdAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss')
    };

    try {
      const prospectRef = doc(db, 'prospects', selectedProspect.id);
      await updateDoc(prospectRef, {
        visitas: arrayUnion(newVisit)
      });

      setSelectedProspect(prev => ({
        ...prev,
        visitas: [...(prev.visitas || []), newVisit]
      }));

      setVisitDate('');
      setVisitNotes('');
      setIsRegisterVisit(false);
    } catch (error) {
      console.error('Error registering visit:', error);
    }
  };

  const handleEditStateClick = () => {
    setIsEditingState(true);
    setNewState(selectedProspect.state);
  };

  const handleSaveStateClick = async () => {
    try {
      const prospectRef = doc(db, 'prospects', selectedProspect.id);
      await updateDoc(prospectRef, { state: newState });
      setSelectedProspect(prev => ({
        ...prev,
        state: newState
      }));
      setIsEditingState(false);
    } catch (error) {
      console.error('Error updating state:', error);
    }
  };

  const handleAddProspect = async () => {
    if (!newProspect.businessName || !newProspect.phone) {
      alert('Please fill in all fields.');
      return;
    }

    try {
      await addDoc(collection(db, 'prospects'), {
        ...newProspect,
        createdAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss')
      });

      setNewProspect({
        businessName: '',
        phone: '',
        state: 'Entrante'
      });
      closeDrawer();
    } catch (error) {
      console.error('Error adding prospect:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProspect(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl mb-4">Prospectos</h1>
      <button
        className="bg-blue-500 text-white px-4 py-2 rounded mb-4"
        onClick={() => setIsDrawerOpen(true) || setIsAddingProspect(true)}
      >
        <FaPlusCircle className="mr-2 inline" /> Agregar Prospecto
      </button>
      <div className="space-y-4">
        {orderedStates.map(state => (
          <div key={state} className="border border-gray-200 rounded-lg">
            <button
              className="w-full text-left p-4 bg-gray-100 font-semibold"
              onClick={() => setShowContent({ ...showContent, [state]: !showContent[state] })}
            >
              {state} ({groupedByState[state].length})
            </button>
            {showContent[state] && (
              <div className="p-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre del Negocio
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groupedByState[state].map(prospect => (
                      <tr key={prospect.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {prospect.businessName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <a href={`https://wa.me/${prospect.phone}`} target="_blank" rel="noopener noreferrer">
                            <FaWhatsapp className="h-6 w-6 text-green-500" />
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            className="text-blue-500 hover:text-blue-700"
                            onClick={() => openDrawer(prospect)}
                          >
                            <FaPlus className="h-6 w-6" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {isDrawerOpen && (
        <div className="fixed inset-0 flex z-50">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" onClick={closeDrawer}></div>
          <div className="relative flex flex-col w-full max-h-full bg-white rounded-t-lg overflow-hidden">
            <div className="absolute top-0 right-0 p-4">
              <button
                className="text-gray-600 hover:text-gray-900"
                onClick={closeDrawer}
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-center">
                <h2 className="text-xl font-bold mb-4">
                  {isAddingProspect ? 'Agregar Nuevo Prospecto' : 'Detalles del Prospecto'}
                </h2>
              </div>
              {isAddingProspect ? (
                <div className="p-4 border-t border-gray-200">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Nombre del Negocio</label>
                    <input
                      type="text"
                      name="businessName"
                      value={newProspect.businessName}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                    <input
                      type="text"
                      name="phone"
                      value={newProspect.phone}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Estado</label>
                    <select
                      name="state"
                      value={newProspect.state}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="Entrante">Entrante</option>
                      <option value="Prospecto calificado">Prospecto calificado</option>
                      <option value="En espera de visita">En espera de visita</option>
                      <option value="Perdido">Perdido</option>
                      <option value="Ganado">Ganado</option>
                    </select>
                  </div>
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600"
                    onClick={handleAddProspect}
                  >
                    Guardar Prospecto
                  </button>
                </div>
              ) : (
                selectedProspect && (
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">Nombre del Negocio</label>
                      <p className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm">
                        {selectedProspect.businessName}
                      </p>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">Contacto</label>
                      <p className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm">
                        {selectedProspect.phone}
                      </p>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">Estado</label>
                      <div className="flex items-center">
                        {isEditingState ? (
                          <select
                            value={newState}
                            onChange={(e) => setNewState(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          >
                            <option value="Entrante">Entrante</option>
                            <option value="Prospecto calificado">Prospecto calificado</option>
                            <option value="En espera de visita">En espera de visita</option>
                            <option value="Perdido">Perdido</option>
                            <option value="Ganado">Ganado</option>
                          </select>
                        ) : (
                          <p className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm">
                            {selectedProspect.state}
                          </p>
                        )}
                        {isEditingState ? (
                          <button
                            className="ml-2 text-blue-500 hover:text-blue-700"
                            onClick={handleSaveStateClick}
                          >
                            <FaSave className="h-5 w-5" />
                          </button>
                        ) : (
                          <button
                            className="ml-2 text-yellow-500 hover:text-yellow-700"
                            onClick={handleEditStateClick}
                          >
                            <FaEdit className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>

                    <h3 className="text-lg font-bold mb-4">Visitas</h3>
                    <button
                      className="bg-green-500 text-white px-4 py-2 rounded shadow hover:bg-green-600 mb-4"
                      onClick={() => setIsRegisterVisit(true)}
                    >
                      Registrar Visita
                    </button>

                    {isRegisterVisit && (
                      <div className="mb-4 p-4 border border-gray-300 rounded-lg">
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700">Fecha y Hora de Visita</label>
                          <input
                            type="datetime-local"
                            value={visitDate}
                            onChange={(e) => setVisitDate(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700">Notas</label>
                          <textarea
                            value={visitNotes}
                            onChange={(e) => setVisitNotes(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          ></textarea>
                        </div>
                        <button
                          className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600"
                          onClick={handleRegisterVisit}
                        >
                          Guardar Visita
                        </button>
                      </div>
                    )}

                    {selectedProspect.visitas && selectedProspect.visitas.length > 0 && (
                      <div>
                        {selectedProspect.visitas.map((visit, index) => (
                          <div key={index} className="mb-4 p-4 border border-gray-300 rounded-lg">
                            <p><strong>Fecha y Hora de Visita:</strong> {visit.date}</p>
                            <p><strong>Notas:</strong> {visit.notes}</p>
                            <p><strong>Fecha de Registro:</strong> {visit.createdAt}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prospectos;
